/* Scroll-snap carousel with page dots. Progressive enhancement:
   without JS the track is still a horizontally scrollable list. */
if (!customElements.get('enyon-carousel')) {
  customElements.define(
    'enyon-carousel',
    class EnyonCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        this.dotsEl = this.querySelector('[data-carousel-dots]');
        this.prevBtn = this.querySelector('[data-carousel-prev]');
        this.nextBtn = this.querySelector('[data-carousel-next]');
        if (!this.track) return;

        this.onScroll = this.throttle(() => this.sync(), 100);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        this.resizeObserver = new ResizeObserver(() => this.build());
        this.resizeObserver.observe(this.track);

        this.prevBtn?.addEventListener('click', () => this.page(-1));
        this.nextBtn?.addEventListener('click', () => this.page(1));

        this.build();
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
      }

      throttle(fn, wait) {
        let queued = false;
        return () => {
          if (queued) return;
          queued = true;
          setTimeout(() => {
            queued = false;
            fn();
          }, wait);
        };
      }

      /* Derive pages from slides-per-view rather than scrollWidth/clientWidth:
         the trailing inter-slide gaps make scrollWidth overshoot a whole page
         and yield one dot too many. */
      get pageCount() {
        const slides = this.track.children.length;
        const first = this.track.firstElementChild;
        if (!slides || !first || !this.track.clientWidth) return 1;
        const slideWidth = first.getBoundingClientRect().width;
        if (!slideWidth) return 1;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const perView = Math.round(((this.track.clientWidth + gap) / (slideWidth + gap)) * 100) / 100;
        return Math.max(1, Math.ceil(slides / perView));
      }

      get currentPage() {
        return Math.round(this.track.scrollLeft / this.track.clientWidth);
      }

      build() {
        const pages = this.pageCount;
        this.toggleAttribute('data-single-page', pages <= 1);

        if (this.dotsEl && this.dotsEl.childElementCount !== pages) {
          this.dotsEl.replaceChildren();
          for (let i = 0; i < pages; i += 1) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'enyon-carousel__dot';
            dot.setAttribute('aria-label', `Go to page ${i + 1} of ${pages}`);
            dot.addEventListener('click', () => this.goTo(i));
            this.dotsEl.append(dot);
          }
        }
        this.sync();
      }

      sync() {
        const pages = this.pageCount;
        const current = Math.min(this.currentPage, pages - 1);

        this.dotsEl?.querySelectorAll('.enyon-carousel__dot').forEach((dot, i) => {
          const active = i === current;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });

        if (this.prevBtn) this.prevBtn.disabled = current <= 0;
        if (this.nextBtn) this.nextBtn.disabled = current >= pages - 1;
      }

      goTo(index) {
        this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
      }

      page(direction) {
        this.goTo(Math.min(Math.max(this.currentPage + direction, 0), this.pageCount - 1));
      }
    }
  );
}
